import { useState, useCallback, useRef } from "react";
import {
  Button,
  makeStyles,
  tokens,
  Tooltip,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Input,
  Toast,
  ToastTitle,
  Toaster,
  useToastController,
  useId,
} from "@fluentui/react-components";
import {
  History24Regular,
  Save24Regular,
  Add24Regular,
} from "@fluentui/react-icons";
import { useChatMode } from "../contexts/ChatModeContext";
import { CopilotChat } from "./CopilotChat";
import { ConversationSidebar } from "./ConversationSidebar";
import { saveConversation } from "../conversation-history";

const useStyles = makeStyles({
  layout: {
    display: "flex",
    flex: 1,
    overflow: "hidden",
  },
  main: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
  },
  chatHeader: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    padding: "0.5rem 1rem",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    flexShrink: 0,
    flexWrap: "wrap",
  },
  actions: {
    display: "flex",
    gap: "0.25rem",
    marginLeft: "auto",
  },
  chatArea: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    position: "relative",
    backgroundColor: "#f5f5f5",
    padding: "0 0 8px 0",
  },
});

// Refs to get messages from the active chat — set by child components
export type ChatRef = {
  getMessages: () => {
    role: "user" | "bot";
    text: string;
    timestamp: string;
  }[];
  hasMessages: () => boolean;
  getThreadId?: () => string | null;
};

let copilotChatRef: ChatRef | null = null;
let openaiChatRef: ChatRef | null = null;

export function setCopilotChatRef(ref: ChatRef | null) {
  copilotChatRef = ref;
}
export function setOpenaiChatRef(ref: ChatRef | null) {
  openaiChatRef = ref;
}

export function ChatLayout() {
  const { mode } = useChatMode();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [conversationVersion, setConversationVersion] = useState(0);
  const [chatKey, setChatKey] = useState(0); // increment to remount chat
  const styles = useStyles();

  // Dialog states
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [saveTitle, setSaveTitle] = useState("");
  const [pendingMessages, setPendingMessages] = useState<any[]>([]);
  const [isSavingFromNewChat, setIsSavingFromNewChat] = useState(false);

  // Toast notifications
  const toasterId = useId("toaster");
  const { dispatchToast } = useToastController(toasterId);

  const showToast = useCallback(
    (message: string, intent: "success" | "error" | "warning" = "success") => {
      dispatchToast(
        <Toast>
          <ToastTitle>{message}</ToastTitle>
        </Toast>,
        { intent, timeout: 3000 },
      );
    },
    [dispatchToast],
  );

  const handleSave = useCallback(async () => {
    const ref = mode === "copilot" ? copilotChatRef : openaiChatRef;
    if (!ref?.hasMessages()) {
      showToast("No messages to save. Start a conversation first!", "warning");
      return;
    }
    const messages = ref.getMessages();
    setPendingMessages(messages);
    setSaveTitle(`Chat ${new Date().toLocaleString()}`);
    setIsSavingFromNewChat(false);
    setSaveDialogOpen(true);
  }, [mode, showToast]);

  const handleSaveConfirm = useCallback(async () => {
    if (!saveTitle.trim()) return;
    setSaveDialogOpen(false);
    try {
      await saveConversation(pendingMessages, saveTitle);
      showToast("Conversation saved!", "success");
      setConversationVersion((v) => v + 1);
      if (isSavingFromNewChat) {
        setChatKey((k) => k + 1);
      }
    } catch (err: any) {
      showToast("Failed to save conversation: " + err.message, "error");
    }
    setPendingMessages([]);
    setSaveTitle("");
    setIsSavingFromNewChat(false);
  }, [saveTitle, pendingMessages, isSavingFromNewChat, showToast]);

  const handleNewChat = useCallback(async () => {
    const ref = mode === "copilot" ? copilotChatRef : openaiChatRef;
    if (ref?.hasMessages()) {
      setConfirmDialogOpen(true);
    } else {
      setChatKey((k) => k + 1);
    }
  }, [mode]);

  const handleConfirmSaveBeforeNew = useCallback(() => {
    setConfirmDialogOpen(false);
    const ref = mode === "copilot" ? copilotChatRef : openaiChatRef;
    const messages = ref?.getMessages() || [];
    setPendingMessages(messages);
    setSaveTitle(`Chat ${new Date().toLocaleString()}`);
    setIsSavingFromNewChat(true);
    setSaveDialogOpen(true);
  }, [mode]);

  const handleSkipSaveAndNew = useCallback(() => {
    setConfirmDialogOpen(false);
    setChatKey((k) => k + 1);
  }, []);

  return (
    <div className={styles.layout}>
      <Toaster toasterId={toasterId} />

      {/* Save Dialog */}
      <Dialog
        open={saveDialogOpen}
        onOpenChange={(_, data) => setSaveDialogOpen(data.open)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Save Conversation</DialogTitle>
            <DialogContent>
              <Input
                placeholder="Enter a title for this conversation"
                value={saveTitle}
                onChange={(_, data) => setSaveTitle(data.value)}
                style={{ width: "100%", marginTop: "8px" }}
              />
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">Cancel</Button>
              </DialogTrigger>
              <Button appearance="primary" onClick={handleSaveConfirm}>
                Save
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Confirm New Chat Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onOpenChange={(_, data) => setConfirmDialogOpen(data.open)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Save before starting new chat?</DialogTitle>
            <DialogContent>
              Do you want to save this conversation before starting a new one?
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={handleSkipSaveAndNew}>
                Don't Save
              </Button>
              <Button appearance="primary" onClick={handleConfirmSaveBeforeNew}>
                Save
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <ConversationSidebar
        open={sidebarOpen}
        onToggle={() => setSidebarOpen((o) => !o)}
        version={conversationVersion}
      />
      <div className={styles.main}>
        <div className={styles.chatHeader}>
          {!sidebarOpen && (
            <Tooltip content="Show history" relationship="label">
              <Button
                appearance="subtle"
                icon={<History24Regular />}
                onClick={() => setSidebarOpen(true)}
                size="small"
              >
                History
              </Button>
            </Tooltip>
          )}
          <div className={styles.actions}>
            <Tooltip content="Save conversation" relationship="label">
              <Button
                appearance="subtle"
                icon={<Save24Regular />}
                onClick={handleSave}
                size="small"
              >
                Save
              </Button>
            </Tooltip>
            <Tooltip content="Start new conversation" relationship="label">
              <Button
                appearance="outline"
                icon={<Add24Regular />}
                onClick={handleNewChat}
                size="small"
              >
                New Chat
              </Button>
            </Tooltip>
          </div>
        </div>
        <div className={styles.chatArea}>
          <CopilotChat key={`copilot-${chatKey}`} />
        </div>
      </div>
    </div>
  );
}
